import React, { useEffect, useRef, useState } from "react";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Toolbar } from "primereact/toolbar";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";

import CommonCal from "../../service/common/CommonCal";
import ApiServices from "../../service/api/ApiServices";

const apiServices = new ApiServices();
const commonCal = new CommonCal();

/* ================= HELPERS ================= */

const normalizePoints = (record) => {
    console.log("Normalizing points from record:", record);
    const points = [];

    if (record.checkin?.latitude && record.checkin?.longitude) {
        points.push({
            latitude: record.checkin.latitude,
            longitude: record.checkin.longitude,
            date: record.checkin.date
        });
    }

    (record.location || []).forEach(loc => {
        points.push({
            latitude: loc.latitude,
            longitude: loc.longitude,
            date: loc.date
        });
    });

    if (record.checkout?.latitude && record.checkout?.longitude) {
        points.push({
            latitude: record.checkout.latitude,
            longitude: record.checkout.longitude,
            date: record.checkout.date
        });
    }

    return points;
};

const haversine = (lat1, lon1, lat2, lon2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(lat1 * Math.PI / 180) *
        Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLon / 2) ** 2;
    return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
};

const calculateDistanceKm = (points) => {
    let total = 0;
    for (let i = 1; i < points.length; i++) {
        total += haversine(
            points[i - 1].latitude,
            points[i - 1].longitude,
            points[i].latitude,
            points[i].longitude
        );
    }
    return Number(total.toFixed(2));
};

const calculateDuration = (points) => {
    if (points.length < 2) return "00:00";

    const start = new Date(points[0].date).getTime();
    const end = new Date(points[points.length - 1].date).getTime();

    const diff = end - start;
    const hrs = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);

    const secs = Math.floor((diff % 60000) / 1000);
    return `${hrs}:${mins}:${secs}`;
};

/* ================= COMPONENT ================= */

export const TechnomanLiveData = () => {

    const durationOptions = [
        { name: "Today", code: "today" },
        { name: "Yesterday", code: "yesterday" },
        { name: "Custom", code: "custom" }
    ];

    const customWise = [
        { name: "Date Wise", code: "datewisesuper" } // SINGLE DATE ONLY
    ];

    const [technomans, setTechnomans] = useState([]);
    const [selectedTechnoman, setSelectedTechnoman] = useState(null);

    const [selectedDuration, setSelectedDuration] = useState(durationOptions[0]);
    const [allDates, setAllDates] = useState(null);

    const [trackingData, setTrackingData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [customFilterDialog, setCustomFilterDialog] = useState(false);
    const [customType, setCustomType] = useState(customWise[0]);
    const [customStart, setCustomStart] = useState("");

    const [summary, setSummary] = useState({
        distanceKm: 0,
        duration: "00:00"
    });

    /* ================= MAP ================= */

    const mapRef = useRef(null);
    const mapInstance = useRef(null);
    const markersRef = useRef([]);
    const polylineRef = useRef(null);

    useEffect(() => {
        if (!window.google || !mapRef.current || mapInstance.current) return;

        mapInstance.current = new window.google.maps.Map(mapRef.current, {
            center: { lat: 20.5937, lng: 78.9629 },
            zoom: 5,
            mapTypeId: window.google.maps.MapTypeId.ROADMAP,
            mapTypeControl: true
        });
    }, []);

    /* ================= INITIAL LOAD ================= */

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        const usersRes = await apiServices.getUsersByType({ userType: "superman" });
        const users = usersRes?.data?.data || [];
        setTechnomans(users);
        if (users.length) setSelectedTechnoman(users[0]);

        const dates = await commonCal.getDates();
        setAllDates(dates);
    };

    /* ================= AUTO FETCH ================= */

    useEffect(() => {
        if (
            selectedTechnoman &&
            allDates &&
            selectedDuration.code !== "custom"
        ) {
            fetchTrackingData();
        }
    }, [selectedTechnoman, selectedDuration, allDates]);

    /* ================= FETCH TRACKING ================= */

    const fetchTrackingData = async (customISODate) => {
        console.log("Fetching tracking data...", selectedDuration, customISODate);
        setLoading(true);
        clearMap();

        let startdate = "";
        let enddate = "";

        if (selectedDuration.code === "today") {
            startdate = allDates.startToday;
            enddate = allDates.endToday;
        }

        if (selectedDuration.code === "yesterday") {
            startdate = allDates.YestStart;
            enddate = allDates.EndStart;
        }

        if (selectedDuration.code === "custom" && customISODate) {
            console.log("Using custom date:", customISODate);
            startdate = customISODate;
            const end = new Date(customISODate);
            end.setHours(23, 59, 59, 999);
            enddate = end.toISOString();
        }

        console.log("Computed date range:", startdate, "to", enddate);

        const payload = {
            userid: selectedTechnoman.userid,
            startDate: startdate,
            endDate: enddate
        };
        console.log("Fetching with payload:", payload);
        const res = await apiServices.getTechnomanLiveData(payload);
        const records = res?.data?.data || [];
        console.log("Fetched records:", records);
        console.log("Total records:", records.length);
        setTrackingData(records);

        if (!records.length) {
            setSummary({ distanceKm: 0, duration: "00:00" });
            setLoading(false);
            return;
        }

        let allPoints = [];

        records.forEach((rec) => {
            allPoints = allPoints.concat(normalizePoints(rec));
        });

        // sort by timestamp (CRITICAL)
        allPoints.sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
        );

        // calculate final metrics
        const distanceKm = calculateDistanceKm(allPoints);
        const duration = calculateDuration(allPoints);

        setSummary({ distanceKm, duration });
        plotTracking(allPoints, distanceKm);

        setLoading(false);
    };


    /* ================= MAP HELPERS ================= */

    const clearMap = () => {
        markersRef.current.forEach(m => m.setMap(null));
        markersRef.current = [];
        if (polylineRef.current) polylineRef.current.setMap(null);
    };

    const addMarker = (pos, color, title) => {
        const marker = new window.google.maps.Marker({
            position: pos,
            map: mapInstance.current,
            title,
            icon: `https://maps.google.com/mapfiles/ms/icons/${color}-dot.png`
        });
        console.log("Added marker at:", pos, "with title:", title, marker);
        markersRef.current.push(marker);
    };

    const plotTracking = (points, distanceKm) => {
        if (!points.length) return;

        const bounds = new window.google.maps.LatLngBounds();
        const path = [];

        points.forEach((p, idx) => {
            const pos = { lat: p.latitude, lng: p.longitude };
            path.push(pos);
            bounds.extend(pos);

            if (idx === 0) addMarker(pos, "green", "Start");
            if (idx === points.length - 1) addMarker(pos, "red", "End");
        });

        polylineRef.current = new window.google.maps.Polyline({
            path,
            strokeColor: "#1e88e5",
            strokeOpacity: 0.9,
            strokeWeight: 4,
            map: mapInstance.current
        });

        mapInstance.current.fitBounds(bounds);
    };



    /* ================= UI ================= */
    const handleDurationChange = (e) => {
        if (e.value.code === "custom") {
            setSelectedDuration(e.value);
            setCustomStart("");
            setCustomFilterDialog(true);
        } else {
            setSelectedDuration(e.value);
        }
    };

    return (
        <>
            <Toolbar
                left={
                    <div className="p-inputgroup">
                        <Button label="Duration" />
                        <Dropdown
                            value={selectedDuration}
                            options={durationOptions}
                            optionLabel="name"
                            onChange={handleDurationChange}
                        />
                    </div>
                }
                right={
                    <div className="p-inputgroup">
                        <Button label="Technoman" />
                        <Dropdown
                            value={selectedTechnoman}
                            options={technomans}
                            optionLabel="name"
                            onChange={(e) => {
                                setSelectedTechnoman(e.value);
                                setSelectedDuration(durationOptions[0]);
                            }}
                        />
                    </div>
                }
            />

            <div style={{ marginTop: 16, position: "relative" }}>
                {loading && (
                    <div className="map-loader">
                        <ProgressSpinner />
                    </div>
                )}

                {!loading && trackingData.length === 0 && (
                    <div className="no-data-overlay">
                        <center><h4>No tracking data is available for the selected day</h4></center>
                    </div>
                )}

                <div ref={mapRef} style={{ height: 450, borderRadius: 12 }} />
            </div>

            <div style={{ marginTop: 10 }}>
                <strong><b>Distance:</b> {summary.distanceKm} km </strong>&nbsp;&nbsp;
                <strong><b>Duration:</b> {summary.duration}</strong>
            </div>

            {/* CUSTOM FILTER DIALOG */}
            <Dialog
                visible={customFilterDialog}
                style={{ width: "550px" }}
                header="Custom Filter"
                onHide={() => setCustomFilterDialog(false)}
                modal
                className="p-fluid"
                maximizable
                blockScroll
            >
                <form
                    onSubmit={(e) => {
                        e.preventDefault();

                        if (!customStart) return;

                        setCustomFilterDialog(false);

                        const start = new Date(customStart);
                        start.setHours(0, 0, 0, 0);

                        fetchTrackingData(start.toISOString());
                    }}

                >
                    <div className="p-grid">
                        <div className="p-col-12">
                            <label>Select Filter Type</label>
                            <Dropdown
                                optionLabel="name"
                                options={customWise}
                                value={customType}
                                disabled
                            />
                        </div>

                        <div className="p-col-12">
                            <label>Select Date</label>
                            <InputText
                                type="date"
                                max={new Date().toISOString().slice(0, 10)}
                                value={customStart}
                                onChange={(e) => setCustomStart(e.target.value)}
                                required
                            />
                        </div>

                        <div className="p-col-12 p-mt-3">
                            <Button type="submit" label="Apply Filter" />
                        </div>
                    </div>
                </form>
            </Dialog>
        </>
    );
};
